import React from 'react'
import {
  ActivityIndicator,
  type GestureResponderEvent,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native'
import {Image} from 'expo-image'
import {type AppBskyEmbedExternal} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {type NavigationProp} from '#/lib/routes/types'
import {
  type EmbedPlayerParams,
  getPlayerAspect,
} from '#/lib/strings/embed-player'
import {useExternalEmbedsPrefs} from '#/state/preferences'
import {EventStopper} from '#/view/com/util/EventStopper'
import {atoms as a, useTheme} from '#/alf'
import {useDialogControl} from '#/components/Dialog'
import {EmbedConsentDialog} from '#/components/dialogs/EmbedConsent'
import {Fill} from '#/components/Fill'
import {useFullscreen} from '#/components/hooks/useFullscreen'
import {PlayButtonIcon} from '#/components/video/PlayButtonIcon'

function PlaceholderOverlay({
  isLoading,
  isPlayerActive,
  onPress,
}: {
  isLoading: boolean
  isPlayerActive: boolean
  onPress: (event: GestureResponderEvent) => void
}) {
  const {_} = useLingui()

  if (isPlayerActive && !isLoading) return null

  return (
    <View style={[a.absolute, a.inset_0, styles.overlayLayer]}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={_(msg`Play Video`)}
        accessibilityHint={_(msg`Plays the video`)}
        onPress={onPress}
        style={styles.overlayContainer}>
        {!isPlayerActive ? (
          <PlayButtonIcon />
        ) : (
          <ActivityIndicator size="large" color="white" />
        )}
      </Pressable>
    </View>
  )
}

function Player({
  params,
  onLoad,
  isPlayerActive,
}: {
  isPlayerActive: boolean
  params: EmbedPlayerParams
  onLoad: () => void
}) {
  if (!isPlayerActive) return null
  return (
    <EventStopper style={[a.absolute, a.inset_0, styles.playerLayer]}>
      <iframe
        src={params.playerUri}
        onLoad={onLoad}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        style={iframeStyle}
      />
    </EventStopper>
  )
}

// Defined outside component to avoid recreation on every render
const iframeStyle: React.CSSProperties = {
  border: 'none',
  width: '100%',
  height: '100%',
  backgroundColor: 'transparent',
  display: 'block',
}

export function ExternalPlayer({
  link,
  params,
}: {
  link: AppBskyEmbedExternal.ViewExternal
  params: EmbedPlayerParams
}) {
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const windowDims = useWindowDimensions()
  const externalEmbedsPrefs = useExternalEmbedsPrefs()
  const consentDialogControl = useDialogControl()
  // Tracks whether the browser is in fullscreen mode (any element)
  const [isFullscreen] = useFullscreen()

  const [isPlayerActive, setPlayerActive] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)

  const aspect = React.useMemo(
    () =>
      getPlayerAspect({
        type: params.type,
        width: windowDims.width,
        hasThumb: !!link.thumb,
      }),
    [params.type, windowDims.width, link.thumb],
  )

  const containerRef = React.useRef<HTMLDivElement>(null)

  // Deactivate player when scrolled out of viewport.
  // The observer is intentionally re-created when isFullscreen changes:
  // during browser fullscreen the element's bounding rect becomes unreliable,
  // so we skip the check entirely to prevent the iframe from being unmounted.
  React.useEffect(() => {
    if (!isPlayerActive || isFullscreen) return
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && !entry.isIntersecting) {
          setPlayerActive(false)
        }
      },
      {threshold: 0},
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isPlayerActive, isFullscreen])

  // Stop player when navigating to another screen
  React.useEffect(() => {
    if (!isPlayerActive) return
    return navigation.addListener('blur', () => setPlayerActive(false))
  }, [navigation, isPlayerActive])

  const onLoad = React.useCallback(() => setIsLoading(false), [])

  const onPlayPress = React.useCallback(
    (event: GestureResponderEvent) => {
      event.preventDefault()
      if (externalEmbedsPrefs?.[params.source] === undefined) {
        consentDialogControl.open()
        return
      }
      setPlayerActive(true)
    },
    [externalEmbedsPrefs, consentDialogControl, params.source],
  )

  const onAcceptConsent = React.useCallback(() => setPlayerActive(true), [])

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    ...aspect,
  }

  return (
    <>
      <EmbedConsentDialog
        control={consentDialogControl}
        source={params.source}
        onAccept={onAcceptConsent}
      />
      <div ref={containerRef} style={containerStyle}>
        {link.thumb && (!isPlayerActive || isLoading) ? (
          <>
            <Image
              style={[a.flex_1]}
              source={{uri: link.thumb}}
              accessibilityIgnoresInvertColors
            />
            <Fill
              style={[
                t.name === 'light' ? t.atoms.bg_contrast_975 : t.atoms.bg,
                {opacity: 0.3},
              ]}
            />
          </>
        ) : (
          <Fill
            style={[
              {
                backgroundColor:
                  t.name === 'light' ? t.palette.contrast_975 : 'black',
                opacity: 0.3,
              },
            ]}
          />
        )}
        <PlaceholderOverlay
          isLoading={isLoading}
          isPlayerActive={isPlayerActive}
          onPress={onPlayPress}
        />
        <Player
          isPlayerActive={isPlayerActive}
          params={params}
          onLoad={onLoad}
        />
      </div>
    </>
  )
}

const styles = StyleSheet.create({
  overlayContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlayLayer: {
    zIndex: 2,
  },
  playerLayer: {
    zIndex: 3,
  },
})
